import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import Header from './Header';
import UserTable from './UserTable';
import UserModal from './UserModal';
import { api } from '../utils/api';
import '../styles/Dashboard.css';

function Dashboard() {
  const { authState } = useOktaAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadUsers();
    }
  }, [authState]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter !== 'all' ? { queue: filter } : {};
      const data = await api.getUsers(params);
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await api.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      alert(`Error deleting user: ${err.message}`);
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      if (editingUser) {
        await api.updateUser(editingUser.user_id, userData);
      } else {
        await api.createUser(userData);
      }
      setShowModal(false);
      await loadUsers();
    } catch (err) {
      throw err; // Let modal handle error display
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadUsers();
    }
  }, [filter]);

  if (!authState?.isAuthenticated) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard">
      <Header />

      <div className="dashboard-content">
        <div className="controls-bar">
          <div className="filter-buttons">
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => handleFilterChange('all')}
            >
              All Users ({users.length})
            </button>
            <button
              className={filter === 'support' ? 'active' : ''}
              onClick={() => handleFilterChange('support')}
            >
              Support Queue
            </button>
            <button
              className={filter === 'crm' ? 'active' : ''}
              onClick={() => handleFilterChange('crm')}
            >
              CRM Queue
            </button>
          </div>

          <button onClick={handleCreateUser} className="btn-primary">
            + Add New User
          </button>
        </div>

        {error && (
          <div className="error-message">
            Error loading users: {error}
            <button onClick={loadUsers}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <UserTable
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
          />
        )}

        {showModal && (
          <UserModal
            user={editingUser}
            onSave={handleSaveUser}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
